import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import portalApi, { apiError } from './portalApi';
import { T } from './portalTheme';
import { usePortalAuth } from '../../context/PortalAuthContext';
import { AuthLayout, Avatar, Button, Spinner, Icon, IC, fmtDate, daysUntil } from './PortalWidgets';

interface InviteStatus {
  recipient_email: string;
  recipient_name: string | null;
  vendor_name: string | null;
  sender_org_name: string | null;
  questionnaire_name: string | null;
  due_date: string | null;
  message: string | null;
  response_id: string;
  account_exists: boolean;
  is_collaborator: boolean;
  kind?: 'remediation';
  destination?: string;
}

// The landing page an invite email's button points to (/questionnaire/portal?invite=<token>), for
// the original recipient and for invited collaborators alike. Shows who's asking and what for,
// then routes to sign-in or account creation -- FluxWatch's equivalent of the screen UpGuard shows
// before "Set a password" / "Get started".
export default function PortalGate() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const invite = params.get('invite') || '';
  const { isAuthenticated, account } = usePortalAuth();

  const [status, setStatus] = useState<InviteStatus | null>(null);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!invite) { setLoadError('This link is missing its invitation code.'); return; }
    portalApi.get('/v1/questionnaire-responses/portal/invite-status', { params: { invite } })
      .then(({ data }) => setStatus(data))
      .catch(err => setLoadError(apiError(err, 'This questionnaire link could not be opened.')));
  }, [invite]);

  useEffect(() => {
    // Already signed in as the invited person? Skip straight to the questionnaire.
    if (isAuthenticated && status && account?.email.toLowerCase() === status.recipient_email.toLowerCase()) {
      navigate(status.destination || `/questionnaire/portal/response/${status.response_id}`, { replace: true });
    }
  }, [isAuthenticated, status, account, navigate]);

  const go = () => {
    if (!status) return;
    navigate(status.account_exists
      ? `/questionnaire/portal/login?invite=${encodeURIComponent(invite)}`
      : `/questionnaire/portal/register?invite=${encodeURIComponent(invite)}`);
  };

  const due = status?.due_date ? daysUntil(status.due_date) : null;

  return (
    <AuthLayout>
      {loadError ? (
        <div style={{ textAlign: 'center' }}>
          <span style={{ width: 56, height: 56, borderRadius: 16, background: T.redBg, color: T.red, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
            <Icon d={IC.alert} size={26} />
          </span>
          <div style={{ fontSize: 22, fontWeight: 800, color: T.ink }}>This link can't be opened</div>
          <div style={{ fontSize: 14, color: T.muted, marginTop: 10, lineHeight: 1.6 }}>{loadError}</div>
          <div style={{ fontSize: 13.5, color: T.muted, marginTop: 14, lineHeight: 1.6 }}>
            Ask the person who sent it for a new invitation, or sign in if you already have an account.
          </div>
          <div style={{ marginTop: 24 }}><Button full variant="secondary" onClick={() => navigate('/questionnaire/portal/login')}>Go to sign in</Button></div>
        </div>
      ) : !status ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}><Spinner size={30} /></div>
      ) : (
        <>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: T.primary, marginBottom: 10 }}>
            {status.kind === 'remediation' ? 'Remediation request' : status.is_collaborator ? 'Invitation to collaborate' : 'Security review request'}
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, color: T.ink, letterSpacing: -0.3, lineHeight: 1.3 }}>
            {status.kind === 'remediation'
              ? `${status.sender_org_name || 'A company'} asked you to fix security risks`
              : status.is_collaborator
              ? `You've been invited to help answer a questionnaire`
              : `${status.sender_org_name || 'A company'} has sent you a security questionnaire`}
          </div>
          <div style={{ fontSize: 14, color: T.muted, marginTop: 10, lineHeight: 1.6 }}>
            {status.kind === 'remediation'
              ? `Their review of ${status.vendor_name || 'your company'} found risks they'd like fixed. Review them, reply, and submit the request once done.`
              : status.is_collaborator
              ? `A colleague at ${status.vendor_name || 'your company'} wants your help completing it.`
              : `They'd like ${status.vendor_name || 'your company'} to complete it as part of their vendor review.`}
          </div>

          <div style={{ marginTop: 24, border: `1px solid ${T.bdr}`, borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: 16, borderBottom: `1px solid ${T.bdr}` }}>
              <Avatar name={status.sender_org_name || '?'} size={40} square />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: T.ink }}>{status.questionnaire_name || (status.kind === 'remediation' ? 'Remediation request' : 'Security questionnaire')}</div>
                <div style={{ fontSize: 13, color: T.muted, marginTop: 2 }}>Requested by {status.sender_org_name || 'a FluxWatch customer'}</div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', background: T.surface2 }}>
              <div style={{ padding: '12px 16px', borderRight: `1px solid ${T.bdr}` }}>
                <div style={{ fontSize: 11.5, fontWeight: 600, color: T.faint, textTransform: 'uppercase', letterSpacing: '.05em' }}>For</div>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: T.ink2, marginTop: 3 }}>{status.vendor_name || '—'}</div>
              </div>
              <div style={{ padding: '12px 16px' }}>
                <div style={{ fontSize: 11.5, fontWeight: 600, color: T.faint, textTransform: 'uppercase', letterSpacing: '.05em' }}>Due</div>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: due !== null && due < 0 ? T.red : due !== null && due <= 7 ? T.amber : T.ink2, marginTop: 3 }}>
                  {status.due_date ? fmtDate(status.due_date) : 'No due date'}
                </div>
              </div>
            </div>
          </div>

          {status.message && (
            <div style={{ marginTop: 16, padding: '12px 14px', borderRadius: 10, background: T.surface2, border: `1px solid ${T.bdr}` }}>
              <div style={{ fontSize: 11.5, fontWeight: 600, color: T.faint, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 5 }}>Message</div>
              <div style={{ fontSize: 13.5, color: T.ink2, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{status.message}</div>
            </div>
          )}

          <div style={{ marginTop: 24 }}>
            <Button size="lg" full onClick={go}>
              {status.account_exists ? 'Sign in to continue' : 'Create a free account to continue'}
            </Button>
          </div>
          <div style={{ fontSize: 12.5, color: T.muted, marginTop: 12, textAlign: 'center', lineHeight: 1.55 }}>
            {status.account_exists
              ? <>You already have an account for <strong>{status.recipient_email}</strong>.</>
              : <>One account for every questionnaire sent to <strong>{status.recipient_email}</strong>, from any company using FluxWatch.</>}
          </div>
        </>
      )}
    </AuthLayout>
  );
}
